import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { enhancedDeliveryApi } from '../apis/endpoints/deliveryApi';
import { selectSellerAddress } from './productSlice';
import { createSelector } from 'reselect';

// Safe way to get the user token or return null
const getUserToken = (getState) => {
  try {
    const state = getState();
    return state.user?.token || null;
  } catch (error) {
    console.warn("Cannot access user token:", error);
    return null;
  }
};

// Async thunks
export const validateAddress = createAsyncThunk(
  'shipping/validateAddress',
  async (addressData, { dispatch, rejectWithValue }) => {
    try {
      console.log('Validating address:', addressData);
      
      // Check if address has all the required fields
      if (!addressData.street || !addressData.city || !addressData.state || !addressData.postalCode) {
        console.warn('Address missing required fields:', addressData);
        return { is_valid: false, address: addressData, validation_results: { messages: ['Address is incomplete'] } };
      }
      
      console.log('Sending address validation request to API');
      const result = await dispatch(
        enhancedDeliveryApi.endpoints.validateAddress.initiate(addressData)
      ).unwrap();
      
      console.log('Address validation result:', result);
      return result;
    } catch (error) {
      console.error('Address validation error:', error);
      return rejectWithValue(error.data || { message: error.message || 'Failed to validate address' });
    }
  }
);

export const calculateShippingRate = createAsyncThunk(
  'shipping/calculateRate',
  async ({ toAddress, parcels }, { dispatch, getState, rejectWithValue }) => {
    console.log('[deliverySlice] calculateShippingRate THUNK received parcels:', JSON.stringify(parcels, null, 2));
    try {
      // Add detailed logging of what we're sending to the API
      console.log('%c 📤 SHIPPING API REQUEST', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 3px;');
      
      // Get seller address from product state
      const state = getState();
      const fromAddress = selectSellerAddress(state);
      
      console.log('Seller address from Redux store:', fromAddress);
      console.log('Customer address:', toAddress);
      console.log('Parcels:', parcels);
      
      // Check if fromAddress and toAddress have all required fields
      if (!fromAddress?.street || !fromAddress?.city || !fromAddress?.state || 
          !(fromAddress?.postalCode || fromAddress?.zipcode)) {
        console.warn('Seller address is incomplete:', fromAddress);
        return rejectWithValue({ message: 'Seller address is incomplete', useEstimator: true });
      }
      
      if (!toAddress?.street || !toAddress?.city || !toAddress?.state || 
          !(toAddress?.postalCode || toAddress?.zipcode)) {
        console.warn('Buyer address is incomplete:', toAddress);
        return rejectWithValue({ message: 'Buyer address is incomplete', useEstimator: true });
      }
      
      // Prepare address formatting for backend
      const fromAddressFormatted = {
        name: fromAddress.name || '',
        company: fromAddress.company || '',
        street: fromAddress.street || '',
        street2: fromAddress.street2 || '',
        city: fromAddress.city || '',
        state: fromAddress.state || '',
        postalCode: fromAddress.postalCode || fromAddress.zipcode || '',
        country: fromAddress.country || 'US',
        latitude: fromAddress.latitude,
        longitude: fromAddress.longitude
      };
      
      const toAddressFormatted = {
        name: toAddress.name || '',
        company: toAddress.company || '',
        street: toAddress.street || '',
        street2: toAddress.street2 || '',
        city: toAddress.city || '',
        state: toAddress.state || '',
        postalCode: toAddress.postalCode || toAddress.zipcode || '',
        country: toAddress.country || 'US',
        latitude: toAddress.latitude,
        longitude: toAddress.longitude
      };
      
      console.log('Formatted addresses for API:');
      console.log('From:', fromAddressFormatted);
      console.log('To:', toAddressFormatted);
      
      // Skip validation for non-US addresses or if bypassing in development environment
      const isNonUSAddress = toAddressFormatted.country !== 'US';
      const bypassValidation = process.env.NODE_ENV === 'development' && process.env.REACT_APP_BYPASS_ADDRESS_VALIDATION === 'true';
      
      if (isNonUSAddress) {
        console.log('Skipping Shippo validation for non-US address:', toAddressFormatted.country);
      } else if (bypassValidation) {
        console.log('Development mode: Bypassing address validation');
      } else {
        // Validate the recipient address (US addresses only)
        try {
          console.log('Validating US recipient address...');
          const toAddressValid = await dispatch(validateAddress(toAddressFormatted)).unwrap();
          
          console.log('Address validation result:', toAddressValid);
          
          // Check if address validation was actually successful
          if (toAddressValid?.validation_results && !toAddressValid.validation_results.is_valid) {
            console.warn('Address validation failed with specific errors:', toAddressValid.validation_results.messages);
            return rejectWithValue({ 
              message: 'Invalid recipient address',
              details: toAddressValid.validation_results.messages,
              useEstimator: true
            });
          }
        } catch (error) {
          console.error('Address validation error:', error);
          return rejectWithValue({
            message: 'Address validation error',
            details: error.message || 'Failed to validate shipping address',
            useEstimator: true
          });
        }
      }
      
      // Calculate shipping rates with the properly formatted addresses
      console.log('Fetching shipping rates...');
      const response = await dispatch(
        enhancedDeliveryApi.endpoints.calculateShippingRates.initiate({
          fromAddress: fromAddressFormatted,
          toAddress: toAddressFormatted,
          parcels
        })
      ).unwrap();
      
      console.log('%c 📥 SHIPPING API RESPONSE', 'background: #2ecc71; color: white; padding: 2px 5px; border-radius: 3px;');
      console.log('Received shipping rates:', response);
      
      // If no rates returned, set flag to use estimator
      if (!response?.rates || response.rates.length === 0) {
        console.warn('No shipping rates returned from API, using estimator');
        return rejectWithValue({ 
          message: 'No shipping rates available for this address', 
          useEstimator: true,
          useEstimatorReason: 'no_rates'
        });
      }
      
      return response.rates;
    } catch (error) {
      console.error('%c ❌ SHIPPING API ERROR', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 3px;');
      console.error('Shipping rate calculation error:', error);
      
      // Add flag to use estimator for all shipping calculation errors
      return rejectWithValue({ 
        message: error.data?.message || error.message || 'Failed to calculate shipping rates',
        useEstimator: true,
        useEstimatorReason: 'api_error'
      });
    }
  }
);

export const generateShippingLabel = createAsyncThunk(
  'shipping/generateLabel',
  async ({ shipment, rateId }, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(
        enhancedDeliveryApi.endpoints.createShippingLabel.initiate({ shipment, rateId })
      ).unwrap();
      return result;
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message || 'Failed to generate shipping label' });
    }
  }
);

export const trackShipment = createAsyncThunk(
  'shipping/trackShipment',
  async ({ carrier, trackingNumber }, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(
        enhancedDeliveryApi.endpoints.trackShipment.initiate({ carrier, trackingNumber })
      ).unwrap();
      return result;
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message || 'Failed to track shipment' });
    }
  }
);

export const confirmPickup = createAsyncThunk(
  'shipping/confirmPickup',
  async ({ fulfillmentGroupId, code }, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(
        enhancedDeliveryApi.endpoints.confirmPickup.initiate({ fulfillmentGroupId, code })
      ).unwrap();
      return result;
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message || 'Failed to confirm pickup' });
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  shippingRates: null,
  selectedRate: null,
  address: null,
  ratesAttempted: false,  // Track whether we've tried to fetch rates
  useEstimator: false,    // Flag to indicate if we should use the GeoShippingEstimator
  estimatorReason: null,   // Reason for using estimator
  shippingLabel: null,
  tracking: null
};

const deliverySlice = createSlice({
  name: 'shipping',
  initialState,
  reducers: {
    setSelectedRate: (state, action) => {
      console.log('Setting selected rate in Redux state:', action.payload?.id);
      // Normalize the rate to ensure it has a consistent ID format
      if (action.payload) {
        // Make sure the rate has an id for consistent comparison
        const normalizedRate = {
          ...action.payload,
          // Ensure consistent property names for display
          provider: action.payload.provider || action.payload.carrier || 'Carrier',
          serviceName: action.payload.servicelevel?.name || action.payload.serviceName || 'Standard',
          amount: action.payload.amount || action.payload.price || '0.00',
          days: action.payload.estimated_days || action.payload.days,
          // Ensure consistent ID format
          id: action.payload.id || 
              action.payload.object_id || 
              `${action.payload.provider || action.payload.carrier}-${action.payload.servicelevel?.name || action.payload.serviceName}-${action.payload.amount || action.payload.price}`
        };
        console.log('Normalized rate:', normalizedRate);
        state.selectedRate = normalizedRate;
      } else {
        state.selectedRate = null;
      }
    },
    clearShippingRates: (state) => {
      state.shippingRates = null;
      state.selectedRate = null;
      state.ratesAttempted = false;
    },
    clearShippingError: (state) => {
      state.error = null;
    },
    // Reset rates attempted flag to allow trying again
    resetRatesAttempted: (state) => {
      state.ratesAttempted = false;
    },
    // Add explicit action to set useEstimator flag
    setUseEstimator: (state, action) => {
      state.useEstimator = action.payload;
      if (action.payload && !state.estimatorReason) {
        state.estimatorReason = 'manual';
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Calculate shipping rate
      .addCase(calculateShippingRate.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.ratesAttempted = true;  // Mark that we've attempted to fetch rates
        state.useEstimator = false;   // Reset estimator flag
        state.estimatorReason = null;
      })
      .addCase(calculateShippingRate.fulfilled, (state, action) => {
        state.loading = false;
        state.shippingRates = action.payload;
        state.ratesAttempted = true;
        state.useEstimator = false;
        state.estimatorReason = null;
        console.log('Setting shipping rates in state:', action.payload);
        
        // Set the cheapest rate as the default selected rate
        if (action.payload && action.payload.length > 0) {
          try {
            // Find the cheapest rate
            let cheapestRate = action.payload.reduce((prev, current) => 
              parseFloat(prev.amount || prev.price || 0) < parseFloat(current.amount || current.price || 0) ? prev : current
            );
            
            // Normalize the rate to ensure it has a consistent ID format and property names
            cheapestRate = {
              ...cheapestRate,
              // Ensure consistent property names for display
              provider: cheapestRate.provider || cheapestRate.carrier || 'Carrier',
              serviceName: cheapestRate.servicelevel?.name || cheapestRate.serviceName || 'Standard',
              amount: cheapestRate.amount || cheapestRate.price || '0.00',
              days: cheapestRate.estimated_days || cheapestRate.days,
              // Ensure consistent ID format
              id: cheapestRate.id || 
                  cheapestRate.object_id || 
                  `${cheapestRate.provider || cheapestRate.carrier}-${cheapestRate.servicelevel?.name || cheapestRate.serviceName}-${cheapestRate.amount || cheapestRate.price}`
            };
            
            state.selectedRate = cheapestRate;
            console.log('Selected cheapest rate:', state.selectedRate);
          } catch (err) {
            console.error('Error selecting cheapest rate:', err);
            
            // Still select the first rate in case of error
            const firstRate = action.payload[0];
            const normalizedFirstRate = {
              ...firstRate,
              // Ensure consistent property names for display
              provider: firstRate.provider || firstRate.carrier || 'Carrier',
              serviceName: firstRate.servicelevel?.name || firstRate.serviceName || 'Standard',
              amount: firstRate.amount || firstRate.price || '0.00',
              days: firstRate.estimated_days || firstRate.days,
              // Ensure consistent ID format
              id: firstRate.id || 
                  firstRate.object_id || 
                  `${firstRate.provider || firstRate.carrier}-${firstRate.servicelevel?.name || firstRate.serviceName}-${firstRate.amount || firstRate.price}`
            };
            
            state.selectedRate = normalizedFirstRate;
          }
        } else {
          state.selectedRate = null;
          console.log('No rates available to select');
        }
      })
      .addCase(calculateShippingRate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { message: 'Failed to calculate shipping rates' };
        state.ratesAttempted = true;  // Mark that we've attempted even if it failed
        state.shippingRates = [];  // Empty array to indicate no rates
        
        // Set flag to use geo estimator if the error indicates it should be used
        state.useEstimator = action.payload?.useEstimator || false;
        state.estimatorReason = action.payload?.useEstimatorReason || 'error';
        
        console.error('Shipping rate calculation rejected:', action.payload);
        console.log('Using geo estimator:', state.useEstimator, 'Reason:', state.estimatorReason);
      })
      // Validate address
      .addCase(validateAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.address = action.payload;
      })
      .addCase(validateAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { message: 'Failed to validate address' };
      })
      // Generate Shipping Label
      .addCase(generateShippingLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(generateShippingLabel.fulfilled, (state, action) => {
        state.loading = false;
        state.shippingLabel = action.payload;
      })
      .addCase(generateShippingLabel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to generate shipping label';
      })
      // Track Shipment
      .addCase(trackShipment.pending, (state) => {
        state.loading = true;
      })
      .addCase(trackShipment.fulfilled, (state, action) => {
        state.loading = false;
        state.trackingInfo = action.payload.tracking;
      })
      .addCase(trackShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to track shipment';
      })
      // Confirm Pickup
      .addCase(confirmPickup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmPickup.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally, update any relevant state based on the action.payload
        // For example, if the fulfillment group details are part of this slice:
        // state.fulfillmentGroup = action.payload;
      })
      .addCase(confirmPickup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to confirm pickup';
      });
  }
});

// Export actions
export const {
  setSelectedRate,
  clearShippingRates,
  clearShippingError,
  resetRatesAttempted,
  setUseEstimator
} = deliverySlice.actions;

export default deliverySlice.reducer;

// Helper function to normalize carrier data
const normalizeRateForConsistency = (rate) => {
  if (!rate) return null;
  
  // Special handling for Colissimo - preserve their object_id
  const id = rate.carrier_name === 'colissimo' ? 
    rate.object_id : 
    rate.id || rate.object_id || `${rate.carrier || rate.provider}-${rate.serviceName || rate.servicelevel?.name}-${rate.amount}`;
  
  return {
    ...rate,
    id,
    object_id: rate.object_id || id,
    provider: rate.provider || rate.carrier_name || rate.carrier || 'Unknown Carrier',
    serviceName: rate.serviceName || rate.servicelevel?.name || 'Standard',
    amount: rate.amount || rate.price || '0.00',
    days: rate.estimated_days || rate.days || '3-5',
    carrier: rate.carrier || rate.provider || '',
    carrier_name: rate.carrier_name || rate.carrier || rate.provider || ''
  };
};

export const selectShippingRate = createSelector(
  [(state) => state.shipping.shippingRates, (state) => state.shipping.selectedRate],
  (rates, selectedRate) => {
    if (!rates || !rates.length) return null;
    
    // If we have a selected rate, find it in the rates
    if (selectedRate) {
      // Check if the selected rate is in the available rates
      const foundRate = rates.find(rate => {
        // Special handling for Colissimo
        if (selectedRate.carrier_name === 'colissimo' || rate.carrier_name === 'colissimo') {
          return selectedRate.object_id === rate.object_id;
        }
        
        // For other carriers, compare ID, or serviceName and amount
        return rate.id === selectedRate.id || 
          (rate.provider === selectedRate.provider && 
           rate.serviceName === selectedRate.serviceName && 
           String(rate.amount) === String(selectedRate.amount));
      });
      
      if (foundRate) {
        return normalizeRateForConsistency(foundRate);
      }
    }
    
    // Otherwise return the cheapest rate
    const sortedRates = [...rates].sort((a, b) => 
      parseFloat(a.amount) - parseFloat(b.amount)
    );
    
    return normalizeRateForConsistency(sortedRates[0]);
  }
); 